import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';
import { Lock, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import TierPaymentModal from '@/components/TierPaymentModal';
import TruncatedText from '@/components/TruncatedText';
import { UnlockCountdown } from '@/components/UnlockCountdown';
import { useTranslatedMap, TranslatableText } from '@/contexts/LanguageContext';

const ModulesPage = ({ user }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTierPaymentModal, setShowTierPaymentModal] = useState(false);
  const [completedModules, setCompletedModules] = useState([]);
  const [membership, setMembership] = useState(null);
  const tr = useTranslatedMap(modules.flatMap((m) => [m.title, m.description]));

  useEffect(() => {
    fetchModules();
    fetchCompletedModules();
    fetchMembership();
  }, []);

  // Refetch modules when membership changes (e.g., after upgrade/comp)
  useEffect(() => {
    if (membership?.membership_tier) {
      console.log('=== TIER CHANGED - REFETCHING MODULES ===');
      console.log('New tier:', membership.membership_tier);
      fetchModules();
    }
  }, [membership?.membership_tier]);

  const fetchModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('=== MODULES LOADED ===');
      console.log('Total modules:', response.data.length);
      console.log('First 3 modules lock status:');
      response.data.slice(0, 3).forEach(m => {
        console.log(`- Module ${m.order}: locked=${m.is_locked}, reason=${m.lock_reason}`);
      });
      setModules(response.data);
    } catch (error) {
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/modules/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedModules(response.data);
    } catch (error) {
      console.error('Failed to load completed modules');
    }
  };

  const fetchMembership = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/membership`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('=== MEMBERSHIP FETCHED ===');
      console.log('Tier:', response.data.membership_tier);
      console.log('Tier Display:', response.data.tier_display);
      console.log('Payment Status:', response.data.payment_status);
      setMembership(response.data);
    } catch (error) {
      console.error('Failed to load membership');
    }
  };

  const isModuleCompleted = (moduleId) => {
    return completedModules.some(c => (typeof c === 'string' ? c : c.module_id) === moduleId);
  };

  const handleCompleteModule = async (moduleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/modules/${moduleId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Module marked as complete!');
      fetchCompletedModules();
      
      // If it's Module 1 and user doesn't have active membership or free_comp, show payment modal
      const module = modules.find(m => m.id === moduleId);
      if (module && module.order === 1) {
        const hasActiveAccess = membership && (membership.payment_status === 'active' || membership.membership_type === 'free_comp');
        if (!hasActiveAccess) {
          setTimeout(() => {
            setShowPaymentModal(true);
          }, 500);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete module');
    }
  };

  const handlePaymentSuccess = () => {
    fetchMembership();
    fetchModules();
    fetchCompletedModules();
    setShowTierPaymentModal(false);
    toast.success('Membership updated! Access granted.');
  };

  if (loading) {
    return <div className="text-center py-20"><TranslatableText text="Loading modules..." /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <TranslatableText as="h1" className="text-4xl sm:text-5xl font-bold text-white mb-3" text="Learning Modules" />
        <TranslatableText as="p" className="text-lg text-gray-300" text="Progress through your spiritual journey at your own pace" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module) => {
          const isFree = module.order === 1;
          const isCompleted = isModuleCompleted(module.id);
          const isLocked = module.is_locked || false;
          const lockReason = module.lock_reason;
          
          // Determine access based on new tier system (used for subtle border styling only)
          const hasAccess = !isLocked;
          
          return (
            <Card 
              key={module.id} 
              data-testid={`module-card-${module.id}`}
              className={`hover-lift border-2 flex flex-col relative ${
                hasAccess
                  ? 'border-cyan-400/30 hover:border-cyan-400'
                  : 'border-gray-300' 
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-xs">
                    Module {module.order}
                  </Badge>
                  {isFree && (
                    <Badge className="bg-green-500 text-white text-xs">
                      FREE
                    </Badge>
                  )}
                  {isCompleted && (
                    <Badge className="bg-cyan-500 text-white text-xs">
                      Completed
                    </Badge>
                  )}
                  {isLocked && (
                    <Badge className="bg-gray-600 text-white text-xs">
                      LOCKED
                    </Badge>
                  )}
                </div>
                {isLocked && module.unlock_date && (
                  <div className="mb-3" data-testid={`module-countdown-${module.order}`}>
                    <UnlockCountdown unlockDate={module.unlock_date} compact tone="light" />
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2 flex items-center gap-2">
                      {tr(module.title)}
                      {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                    </CardTitle>
                  </div>
                </div>
                <CardDescription className="mt-3">
                  <TruncatedText text={tr(module.description)} maxLength={250} />
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto space-y-3 pb-6">
                <Link to={`/dashboard/modules/${module.id}`} className="w-full block" data-testid={`module-enter-${module.id}`}>
                  {/* On a tier-locked card the upgrade is the primary action; entering still works
                      (it shows what's inside) but must not compete for the eye. */}
                  <button
                    className={
                      isLocked && lockReason === 'tier_insufficient'
                        ? 'w-full flex items-center justify-center gap-2 rounded-full border border-[#331a67]/30 bg-transparent px-4 py-2.5 text-sm text-[#331a67] transition-colors duration-200 hover:border-[#331a67] hover:bg-[#331a67]/5'
                        : 'btn-primary w-full flex items-center justify-center gap-2'
                    }
                  >
                    <span>{isLocked && lockReason === 'tier_insufficient' ? 'Preview Module' : 'Enter Module'}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                {isFree && !isCompleted && (
                  <Button 
                    onClick={() => handleCompleteModule(module.id)}
                    className="w-full"
                    variant="outline"
                    style={{ borderColor: '#331a67', color: '#331a67' }}
                  >
                    Mark as Complete
                  </Button>
                )}
                {isLocked && lockReason === 'drip_feed' && (
                  <div
                    className="flex items-center justify-center gap-2 text-sm text-gray-500 pt-1"
                    data-testid={`module-lock-notice-${module.id}`}
                  >
                    <Lock className="w-4 h-4" />
                    <span>
                      {module.unlock_date
                        ? `Opens ${new Date(module.unlock_date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`
                        : 'Complete previous module to unlock content'}
                    </span>
                  </div>
                )}
                {isLocked && lockReason === 'tier_insufficient' && (
                  <div className="space-y-2 pt-1" data-testid={`module-lock-notice-${module.id}`}>
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <Lock className="w-4 h-4" />
                      <span>Upgrade your membership to unlock</span>
                    </div>
                    <Button
                      onClick={() => setShowTierPaymentModal(true)}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                      data-testid={`module-upgrade-btn-${module.id}`}
                    >
                      Upgrade to Unlock
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <TierPaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        currentTier={user?.membership_tier || 'bronze'}
      />

      <TierPaymentModal
        isOpen={showTierPaymentModal}
        onClose={() => setShowTierPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        currentTier={membership?.membership_tier || 'bronze'}
      />

      {modules.length === 0 && (
        <div className="text-center py-20">
          <p className="text-gray-500 text-lg"><TranslatableText text="No modules available yet. Check back soon!" /></p>
        </div>
      )}
    </div>
  );
};

export default ModulesPage;